import { useCallback, useContext, useEffect, useState } from "react";
import { useEditCandidateProfileMutation } from "../../../../App/Queries/CandidateQueries";
import { ICandidatePhoneNumber, IEditCandidatePhoneNumber } from "../../../../App/Typings/Candidate";
import { CandidateContext } from "../../../CandidateProfile";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { CandidatePhoneNumberType } from "../../../../App/Enums/CandidateEnums";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";

export const useEditPhoneState = (candidatePhoneNumber: ICandidatePhoneNumber) => {
  const { candidate } = useContext(CandidateContext);
  const { displaySuccess, displayError, displayInfo } = useNotification();
  const { mutateAsync: editProfileAsync, isLoading } = useEditCandidateProfileMutation();
  const { id, phoneNumber, phoneType, isValid } = candidatePhoneNumber;
  const [isEditPhone, setIsEditPhone] = useState(false);
  const [editPhone, setEditPhone] = useState<IEditCandidatePhoneNumber>({
    phoneNumberId: id,
    phoneNumberRaw: phoneNumber,
    phoneType: CandidatePhoneNumberType[phoneType],
  });

  // Update state when id gets updated after number is added
  useEffect(() => {
    setEditPhone((prev) => ({
      ...prev,
      phoneNumberId: id,
    }));
  }, [id]);

  const validPhoneNumberEdit = validateEditPhoneNumber(editPhone);
  const changedPhoneNumber = editPhone.phoneNumberRaw !== phoneNumber;
  const changedPhoneType = editPhone.phoneType !== CandidatePhoneNumberType[phoneType];
  const disableSave = isLoading || !validPhoneNumberEdit || (!changedPhoneNumber && !changedPhoneType);
  let saveButtonMessage = "Save Changes";
  if (isLoading) {
    saveButtonMessage = "Saving...";
  } else if (!validPhoneNumberEdit) {
    saveButtonMessage = "Invalid phone number";
  } else if (!changedPhoneNumber && !changedPhoneType) {
    saveButtonMessage = "No changes made to phone number or type";
  }

  function handleChangePhoneNumber(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setEditPhone((edit) => ({ ...edit, phoneNumberRaw: e.target.value }));
  }
  function handleChangePhoneType(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setEditPhone((edit) => ({
      ...edit,
      phoneType: CandidatePhoneNumberType[Number(e.target.value)],
    }));
  }
  const submitEditPhone = useCallback(async () => {
    if (disableSave) {
      handleCancelChanges();
      displayInfo("No changes made to phone number or type.");
      return;
    }
    await editProfileAsync(
      {
        candidateId: candidate.id,
        emailAddress: null,
        phoneNumber: editPhone,
        tags: null,
      },
      {
        onError: () => {
          displayError("Error updating phone number");
        },
        onSettled: (response) => {
          if (response?.success === true) {
            setIsEditPhone(false);
            displaySuccess("Successfully updated phone number");
          } else if (response?.errorMessage) {
            displayError(response?.errorMessage);
          } else {
            displayError("There was an error updating phone number");
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPhone.phoneNumberRaw, editPhone.phoneType]);

  const handleCancelChanges = () => {
    setEditPhone({
      phoneNumberId: id,
      phoneNumberRaw: phoneNumber,
      phoneType: CandidatePhoneNumberType[phoneType],
    });
    setIsEditPhone(false);
  };

  const handleSubmit = useDebouncedHandler(() => {
    submitEditPhone();
  }, 300);

  return {
    getters: {
      isEditPhone,
      isValid,
      editPhone,
      disableSave,
      phoneNumber,
      phoneType,
      saveButtonMessage,
      validPhoneNumberEdit,
    },
    handlers: {
      setIsEditPhone,
      handleChangePhoneNumber,
      handleChangePhoneType,
      handleSubmit,
      handleCancelChanges,
    },
  } as const;
};

function validateEditPhoneNumber(editEmail: IEditCandidatePhoneNumber): boolean {
  if (!editEmail.phoneNumberRaw) {
    return false;
  }
  return true;
}

export const phoneNumberTypeOptions = Object.keys(CandidatePhoneNumberType)
  .filter((key) => Number(key) >= 0)
  .map((key) => ({
    value: key,
    label: CandidatePhoneNumberType[key as keyof typeof CandidatePhoneNumberType],
  }));

export function convertStringToCandidatePhoneNumberType(phoneNumberType: string): CandidatePhoneNumberType {
  switch (phoneNumberType) {
    case "Home":
      return CandidatePhoneNumberType.Home;
    case "Mobile":
      return CandidatePhoneNumberType.Mobile;
    case "Skype":
      return CandidatePhoneNumberType.Skype;
    case "Work":
      return CandidatePhoneNumberType.Work;
    case "Other":
      return CandidatePhoneNumberType.Other;
    default:
      return CandidatePhoneNumberType.Other;
  }
}
